/* eslint-disable no-unused-vars */
/**
 * Pre-built play templates with players, motion paths, speeds and delays
 * already calibrated. Coaches can apply any template as a starting point
 * for a new play and tweak from there.
 *
 * Coordinate system: half-court normalized 0..100.
 *   y =   0 → half-court line (top of canvas)
 *   y = 100 → baseline (bottom of canvas, where the basket sits)
 *   x = 50  → center of the court
 */

const offPlayer = (id, label, x, y, name) => ({
  id,
  label,
  team: 'offense',
  position: { x, y },
  photo_url: null,
  player_id: null,
  player_name: name || null,
});

const defPlayer = (id, label, x, y) => ({
  id,
  label,
  team: 'defense',
  position: { x, y },
  photo_url: null,
  player_id: null,
  player_name: null,
});

/** Smooth arc between two points (3 control points) */
function arcPath(a, b, curve = 0) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  // Offset the midpoint perpendicular to the segment to bend the path
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const cx = mx + nx * curve;
  const cy = my + ny * curve;
  return [a, { x: cx, y: cy }, b];
}

/** Linear path between two points (5 control points) */
function linePath(a, b) {
  const pts = [];
  for (let i = 0; i <= 4; i++) {
    const t = i / 4;
    pts.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return pts;
}

// ---------------------------------------------------------------------------
//  PICK & ROLL  (5-out, side PnR)
// ---------------------------------------------------------------------------
const pickRoll = {
  id: 'tpl-pnr',
  name: 'Pick & Roll',
  category: 'man',
  difficulty: 'beginner',
  description:
    'Pick & Roll lateral clásico. El 5 sube a poner bloqueo al base, el 1 ataca usando el bloqueo y el 5 rola al aro.',
  works_against: ['man'],
  success_rate: 0.72,
  steps: [
    {
      step_number: 1,
      description: 'Formación inicial 5-out. El 1 tiene el balón en el top.',
      players: [
        offPlayer('o1', '1', 50, 15, 'Base'),
        offPlayer('o2', '2', 82, 45),
        offPlayer('o3', '3', 18, 45),
        offPlayer('o4', '4', 72, 78),
        offPlayer('o5', '5', 28, 78, 'Pívot'),
        defPlayer('d1', 'X1', 50, 25),
        defPlayer('d5', 'X5', 28, 70),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'El 5 sube a poner bloqueo lateral. El 1 dribla buscando el bloqueo por encima.',
      players: [
        offPlayer('o1', '1', 56, 22, 'Base'),
        offPlayer('o2', '2', 82, 45),
        offPlayer('o3', '3', 18, 45),
        offPlayer('o4', '4', 72, 78),
        offPlayer('o5', '5', 50, 28, 'Pívot'),
        defPlayer('d1', 'X1', 50, 25),
        defPlayer('d5', 'X5', 50, 35),
      ],
      arrows: [],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 50, y: 15 }, { x: 56, y: 22 }, 4), speed: 0.9, delay: 0 },
        o5: { points: linePath({ x: 28, y: 78 }, { x: 50, y: 28 }), speed: 1.1, delay: 0.1 },
      },
    },
    {
      step_number: 3,
      description: 'El 5 rola al aro tras el bloqueo, el 1 ataca y lee el cambio defensivo.',
      players: [
        offPlayer('o1', '1', 55, 50, 'Base'),
        offPlayer('o2', '2', 82, 45),
        offPlayer('o3', '3', 18, 45),
        offPlayer('o4', '4', 72, 78),
        offPlayer('o5', '5', 50, 75, 'Pívot'),
        defPlayer('d1', 'X1', 60, 35),
        defPlayer('d5', 'X5', 55, 55),
      ],
      arrows: [
        { from: { x: 55, y: 50 }, to: { x: 50, y: 75 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 56, y: 22 }, { x: 55, y: 50 }, -6), speed: 1.0, delay: 0 },
        o5: { points: arcPath({ x: 50, y: 28 }, { x: 50, y: 75 }, 0), speed: 1.2, delay: 0.3 },
      },
    },
  ],
};

// ---------------------------------------------------------------------------
//  HORNS  (1-4-set with bigs at the elbows)
// ---------------------------------------------------------------------------
const horns = {
  id: 'tpl-horns',
  name: 'Horns',
  category: 'man',
  difficulty: 'intermediate',
  description:
    'Sistema Horns. Bigs en los codos, esquinas pobladas. Doble bloqueo para el base, el 5 rola y el 4 abre al triple.',
  works_against: ['man'],
  success_rate: 0.68,
  steps: [
    {
      step_number: 1,
      description: 'Formación Horns: 1 en el top, 4 y 5 en los codos, 2 y 3 en las esquinas.',
      players: [
        offPlayer('o1', '1', 50, 18, 'Base'),
        offPlayer('o4', '4', 38, 40, 'Ala-pívot'),
        offPlayer('o5', '5', 62, 40, 'Pívot'),
        offPlayer('o2', '2', 15, 85),
        offPlayer('o3', '3', 85, 85),
        defPlayer('d1', 'X1', 50, 28),
        defPlayer('d4', 'X4', 38, 48),
        defPlayer('d5', 'X5', 62, 48),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'Doble bloqueo de 4 y 5 sobre el defensor del 1. El base ataca el centro.',
      players: [
        offPlayer('o1', '1', 50, 32, 'Base'),
        offPlayer('o4', '4', 44, 35, 'Ala-pívot'),
        offPlayer('o5', '5', 56, 35, 'Pívot'),
        offPlayer('o2', '2', 15, 85),
        offPlayer('o3', '3', 85, 85),
        defPlayer('d1', 'X1', 50, 28),
        defPlayer('d4', 'X4', 38, 48),
        defPlayer('d5', 'X5', 62, 48),
      ],
      arrows: [],
      strokes: [],
      paths: {
        o1: { points: linePath({ x: 50, y: 18 }, { x: 50, y: 32 }), speed: 1.0, delay: 0 },
        o4: { points: linePath({ x: 38, y: 40 }, { x: 44, y: 35 }), speed: 0.8, delay: 0 },
        o5: { points: linePath({ x: 62, y: 40 }, { x: 56, y: 35 }), speed: 0.8, delay: 0 },
      },
    },
    {
      step_number: 3,
      description: 'El 5 rola al aro, el 4 abre al triple ("Roll & Replace"). El 1 lee al defensor.',
      players: [
        offPlayer('o1', '1', 50, 42, 'Base'),
        offPlayer('o4', '4', 28, 25, 'Ala-pívot'),
        offPlayer('o5', '5', 58, 78, 'Pívot'),
        offPlayer('o2', '2', 15, 85),
        offPlayer('o3', '3', 85, 85),
        defPlayer('d1', 'X1', 50, 36),
        defPlayer('d4', 'X4', 40, 38),
        defPlayer('d5', 'X5', 60, 58),
      ],
      arrows: [
        { from: { x: 50, y: 42 }, to: { x: 28, y: 25 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o1: { points: linePath({ x: 50, y: 32 }, { x: 50, y: 42 }), speed: 1.0, delay: 0 },
        o4: { points: arcPath({ x: 44, y: 35 }, { x: 28, y: 25 }, -5), speed: 1.1, delay: 0.2 },
        o5: { points: arcPath({ x: 56, y: 35 }, { x: 58, y: 78 }, 4), speed: 1.2, delay: 0.2 },
      },
    },
  ],
};

// ---------------------------------------------------------------------------
//  SPAIN PnR  (Stack PnR — back-screen on roller's defender)
// ---------------------------------------------------------------------------
const spainPnr = {
  id: 'tpl-spain',
  name: 'Spain PnR',
  category: 'man',
  difficulty: 'advanced',
  description:
    'Spain PnR (Stack PnR). El 5 pone bloqueo directo al 1; el 4 hace back-screen al defensor del 5 cuando rola. Triple amenaza: penetración, rol al aro o pop al triple.',
  works_against: ['man'],
  success_rate: 0.74,
  steps: [
    {
      step_number: 1,
      description: 'Formación apilada. 1 con balón, 5 en cabeza del área, 4 detrás del 5 listo para back-screen.',
      players: [
        offPlayer('o1', '1', 50, 15, 'Base'),
        offPlayer('o5', '5', 50, 32, 'Pívot'),
        offPlayer('o4', '4', 50, 52, 'Ala-pívot'),
        offPlayer('o2', '2', 15, 80),
        offPlayer('o3', '3', 85, 80),
        defPlayer('d1', 'X1', 50, 25),
        defPlayer('d5', 'X5', 50, 40),
        defPlayer('d4', 'X4', 50, 60),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'El 5 pone el bloqueo. El 1 ataca por la derecha. El 4 se prepara para el back-screen.',
      players: [
        offPlayer('o1', '1', 60, 26, 'Base'),
        offPlayer('o5', '5', 50, 32, 'Pívot'),
        offPlayer('o4', '4', 50, 52, 'Ala-pívot'),
        offPlayer('o2', '2', 15, 80),
        offPlayer('o3', '3', 85, 80),
        defPlayer('d1', 'X1', 56, 30),
        defPlayer('d5', 'X5', 55, 36),
        defPlayer('d4', 'X4', 50, 60),
      ],
      arrows: [],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 50, y: 15 }, { x: 60, y: 26 }, 4), speed: 1.0, delay: 0 },
      },
    },
    {
      step_number: 3,
      description: 'El 5 rola; el 4 pone back-screen al X5 y pop al triple. Tres opciones para el 1: tiro, rol o pop.',
      players: [
        offPlayer('o1', '1', 55, 48, 'Base'),
        offPlayer('o5', '5', 52, 78, 'Pívot'),
        offPlayer('o4', '4', 45, 18, 'Ala-pívot'),
        offPlayer('o2', '2', 15, 80),
        offPlayer('o3', '3', 85, 80),
        defPlayer('d1', 'X1', 60, 36),
        defPlayer('d5', 'X5', 50, 55),
        defPlayer('d4', 'X4', 48, 55),
      ],
      arrows: [
        { from: { x: 55, y: 48 }, to: { x: 52, y: 78 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 60, y: 26 }, { x: 55, y: 48 }, -4), speed: 1.0, delay: 0 },
        o5: { points: arcPath({ x: 50, y: 32 }, { x: 52, y: 78 }, 3), speed: 1.2, delay: 0.25 },
        o4: { points: arcPath({ x: 50, y: 52 }, { x: 45, y: 18 }, -8), speed: 1.1, delay: 0.3 },
      },
    },
  ],
};

// ---------------------------------------------------------------------------
//  FLEX OFFENSE  (continuity — flex cut + down screen)
// ---------------------------------------------------------------------------
const flex = {
  id: 'tpl-flex',
  name: 'Flex',
  category: 'man',
  difficulty: 'intermediate',
  description:
    'Sistema de continuidad Flex. Corte flex desde la esquina hacia el aro usando un bloqueo del poste bajo, seguido de un down-screen para el cortador. Genera triples y bandejas al cambiar el lado.',
  works_against: ['man'],
  success_rate: 0.66,
  steps: [
    {
      step_number: 1,
      description: 'Formación inicial 4-out: base en el top, 2 alas y 2 esquinas. El 5 en poste bajo.',
      players: [
        offPlayer('o1', '1', 50, 18, 'Base'),
        offPlayer('o2', '2', 80, 35),
        offPlayer('o3', '3', 20, 35),
        offPlayer('o4', '4', 18, 88, 'Esquina'),
        offPlayer('o5', '5', 35, 75, 'Pívot'),
        defPlayer('d1', 'X1', 50, 28),
        defPlayer('d4', 'X4', 22, 85),
        defPlayer('d5', 'X5', 38, 73),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'Pase de 1 a 3. El 4 corta flex desde la esquina usando el bloqueo del 5.',
      players: [
        offPlayer('o1', '1', 50, 22, 'Base'),
        offPlayer('o2', '2', 80, 35),
        offPlayer('o3', '3', 20, 35),
        offPlayer('o4', '4', 60, 80, 'Esquina'),
        offPlayer('o5', '5', 35, 75, 'Pívot'),
        defPlayer('d1', 'X1', 50, 30),
        defPlayer('d4', 'X4', 40, 80),
        defPlayer('d5', 'X5', 38, 73),
      ],
      arrows: [
        { from: { x: 50, y: 22 }, to: { x: 20, y: 35 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o4: { points: linePath({ x: 18, y: 88 }, { x: 60, y: 80 }), speed: 1.2, delay: 0.1 },
      },
    },
    {
      step_number: 3,
      description: 'Down-screen: el 1 baja a poner pantalla al 5 que sube al codo. Tiro abierto o pase al cortador.',
      players: [
        offPlayer('o1', '1', 35, 60, 'Base'),
        offPlayer('o2', '2', 80, 35),
        offPlayer('o3', '3', 20, 35),
        offPlayer('o4', '4', 60, 75, 'Esquina'),
        offPlayer('o5', '5', 40, 40, 'Pívot'),
        defPlayer('d1', 'X1', 38, 65),
        defPlayer('d4', 'X4', 56, 76),
        defPlayer('d5', 'X5', 42, 50),
      ],
      arrows: [
        { from: { x: 20, y: 35 }, to: { x: 40, y: 40 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 50, y: 22 }, { x: 35, y: 60 }, -5), speed: 1.0, delay: 0.1 },
        o5: { points: linePath({ x: 35, y: 75 }, { x: 40, y: 40 }), speed: 1.1, delay: 0.3 },
      },
    },
  ],
};

// ---------------------------------------------------------------------------
//  PRINCETON CHIN  (back-cut + dribble hand-off)
// ---------------------------------------------------------------------------
const princetonChin = {
  id: 'tpl-princeton-chin',
  name: 'Princeton Chin',
  category: 'man',
  difficulty: 'advanced',
  description:
    'Acción Chin del sistema Princeton. Inicia con pase al codo, back-cut del pasador y luego dribble hand-off para el ala. Crea bandejas si el defensor sobre-juega y triples si va por debajo.',
  works_against: ['man'],
  success_rate: 0.70,
  steps: [
    {
      step_number: 1,
      description: 'Formación 1-2-2 Princeton. 1 con balón, 2 y 3 en alas altas, 4 y 5 en codos.',
      players: [
        offPlayer('o1', '1', 50, 18, 'Base'),
        offPlayer('o2', '2', 80, 32),
        offPlayer('o3', '3', 20, 32),
        offPlayer('o4', '4', 38, 45, 'Codo'),
        offPlayer('o5', '5', 62, 45, 'Codo'),
        defPlayer('d1', 'X1', 50, 28),
        defPlayer('d4', 'X4', 38, 53),
        defPlayer('d5', 'X5', 62, 53),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'Pase de 1 al codo (5). El 1 hace back-cut hacia el aro buscando bandeja.',
      players: [
        offPlayer('o1', '1', 55, 70, 'Base'),
        offPlayer('o2', '2', 80, 32),
        offPlayer('o3', '3', 20, 32),
        offPlayer('o4', '4', 38, 45, 'Codo'),
        offPlayer('o5', '5', 62, 45, 'Codo'),
        defPlayer('d1', 'X1', 52, 65),
        defPlayer('d4', 'X4', 38, 53),
        defPlayer('d5', 'X5', 62, 53),
      ],
      arrows: [
        { from: { x: 50, y: 18 }, to: { x: 62, y: 45 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 50, y: 18 }, { x: 55, y: 70 }, 8), speed: 1.3, delay: 0.1 },
      },
    },
    {
      step_number: 3,
      description: 'Dribble hand-off del 5 al 2. El 2 ataca y lee penetración, triple o pase al 1 si tiene el aro.',
      players: [
        offPlayer('o1', '1', 55, 70, 'Base'),
        offPlayer('o2', '2', 62, 45),
        offPlayer('o3', '3', 20, 32),
        offPlayer('o4', '4', 38, 45, 'Codo'),
        offPlayer('o5', '5', 70, 50, 'Codo'),
        defPlayer('d1', 'X1', 52, 65),
        defPlayer('d4', 'X4', 38, 53),
        defPlayer('d5', 'X5', 65, 50),
      ],
      arrows: [
        { from: { x: 62, y: 45 }, to: { x: 55, y: 70 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o2: { points: arcPath({ x: 80, y: 32 }, { x: 62, y: 45 }, -3), speed: 1.1, delay: 0.2 },
        o5: { points: linePath({ x: 62, y: 45 }, { x: 70, y: 50 }), speed: 0.9, delay: 0.2 },
      },
    },
  ],
};

// ---------------------------------------------------------------------------
//  ZIPPER  (down-screen for shooting guard cutting up to receive)
// ---------------------------------------------------------------------------
const zipper = {
  id: 'tpl-zipper',
  name: 'Zipper',
  category: 'man',
  difficulty: 'beginner',
  description:
    'Zipper action: el 5 pone un down-screen para que el 2 suba "como una cremallera" al top a recibir el pase. Inicio simple para entrar en cualquier sistema (PnR, hand-off).',
  works_against: ['man'],
  success_rate: 0.69,
  steps: [
    {
      step_number: 1,
      description: 'El 1 con balón a un lado. El 2 en la esquina opuesta. El 5 en poste bajo.',
      players: [
        offPlayer('o1', '1', 25, 30, 'Base'),
        offPlayer('o2', '2', 50, 88),
        offPlayer('o3', '3', 80, 30),
        offPlayer('o4', '4', 75, 78),
        offPlayer('o5', '5', 50, 70, 'Pívot'),
        defPlayer('d1', 'X1', 28, 38),
        defPlayer('d2', 'X2', 50, 82),
        defPlayer('d5', 'X5', 50, 65),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'El 5 sube a poner el down-screen. El 2 sube usando el bloqueo en línea recta.',
      players: [
        offPlayer('o1', '1', 25, 30, 'Base'),
        offPlayer('o2', '2', 50, 30),
        offPlayer('o3', '3', 80, 30),
        offPlayer('o4', '4', 75, 78),
        offPlayer('o5', '5', 50, 55, 'Pívot'),
        defPlayer('d1', 'X1', 28, 38),
        defPlayer('d2', 'X2', 50, 75),
        defPlayer('d5', 'X5', 52, 50),
      ],
      arrows: [],
      strokes: [],
      paths: {
        o2: { points: linePath({ x: 50, y: 88 }, { x: 50, y: 30 }), speed: 1.3, delay: 0 },
        o5: { points: linePath({ x: 50, y: 70 }, { x: 50, y: 55 }), speed: 0.9, delay: 0 },
      },
    },
    {
      step_number: 3,
      description: 'Pase del 1 al 2 en el top. El 5 sigue al PnR. El 2 lee defensa: tiro o ataque.',
      players: [
        offPlayer('o1', '1', 25, 30, 'Base'),
        offPlayer('o2', '2', 50, 28),
        offPlayer('o3', '3', 80, 30),
        offPlayer('o4', '4', 75, 78),
        offPlayer('o5', '5', 50, 38, 'Pívot'),
        defPlayer('d1', 'X1', 28, 38),
        defPlayer('d2', 'X2', 50, 38),
        defPlayer('d5', 'X5', 50, 45),
      ],
      arrows: [
        { from: { x: 25, y: 30 }, to: { x: 50, y: 28 }, type: 'pass' },
      ],
      strokes: [],
      paths: {
        o5: { points: linePath({ x: 50, y: 55 }, { x: 50, y: 38 }), speed: 1.0, delay: 0.2 },
      },
    },
  ],
};

// ---------------------------------------------------------------------------
//  STACK BLOB  (baseline out-of-bounds — stack triple screen)
// ---------------------------------------------------------------------------
const stack = {
  id: 'tpl-stack',
  name: 'Stack BLOB',
  category: 'blob',
  difficulty: 'intermediate',
  description:
    'Stack BLOB clásico: 3 jugadores apilados en la línea de fondo, el saque-fondo escoge: corte al aro del primero, mid-range del segundo, triple del tercero. Triple amenaza desde el saque de banda.',
  works_against: ['man'],
  success_rate: 0.71,
  steps: [
    {
      step_number: 1,
      description: 'Pívot (5) saca el balón fuera de banda. 1, 2 y 4 apilados frente al aro. El 3 en el lado contrario.',
      players: [
        offPlayer('o5', '5', 70, 96, 'Saque-fondo'),
        offPlayer('o1', '1', 50, 78, 'Base'),
        offPlayer('o2', '2', 50, 70, 'Tirador'),
        offPlayer('o4', '4', 50, 62, 'Ala-pívot'),
        offPlayer('o3', '3', 18, 70, 'Ala'),
        defPlayer('d1', 'X1', 50, 84),
        defPlayer('d2', 'X2', 52, 75),
        defPlayer('d4', 'X4', 52, 60),
        defPlayer('d3', 'X3', 20, 75),
      ],
      arrows: [],
      strokes: [],
      paths: {},
    },
    {
      step_number: 2,
      description: 'El 1 corta al aro por la línea de fondo. El 2 abre a la esquina. El 4 sube al top.',
      players: [
        offPlayer('o5', '5', 70, 96, 'Saque-fondo'),
        offPlayer('o1', '1', 30, 90, 'Base'),
        offPlayer('o2', '2', 88, 88, 'Tirador'),
        offPlayer('o4', '4', 50, 35, 'Ala-pívot'),
        offPlayer('o3', '3', 18, 70, 'Ala'),
        defPlayer('d1', 'X1', 40, 88),
        defPlayer('d2', 'X2', 80, 85),
        defPlayer('d4', 'X4', 52, 45),
        defPlayer('d3', 'X3', 20, 75),
      ],
      arrows: [],
      strokes: [],
      paths: {
        o1: { points: arcPath({ x: 50, y: 78 }, { x: 30, y: 90 }, 5), speed: 1.3, delay: 0 },
        o2: { points: arcPath({ x: 50, y: 70 }, { x: 88, y: 88 }, -8), speed: 1.2, delay: 0.1 },
        o4: { points: linePath({ x: 50, y: 62 }, { x: 50, y: 35 }), speed: 1.1, delay: 0.2 },
      },
    },
    {
      step_number: 3,
      description: 'El 5 saca al 4 en el top (o lee al 1 bajo el aro / 2 en esquina si la defensa colapsa).',
      players: [
        offPlayer('o5', '5', 70, 92, 'Saque-fondo'),
        offPlayer('o1', '1', 28, 88, 'Base'),
        offPlayer('o2', '2', 88, 88, 'Tirador'),
        offPlayer('o4', '4', 50, 32, 'Ala-pívot'),
        offPlayer('o3', '3', 18, 70, 'Ala'),
        defPlayer('d1', 'X1', 38, 88),
        defPlayer('d2', 'X2', 80, 85),
        defPlayer('d4', 'X4', 50, 40),
        defPlayer('d3', 'X3', 20, 75),
      ],
      arrows: [
        { from: { x: 70, y: 92 }, to: { x: 50, y: 32 }, type: 'pass' },
      ],
      strokes: [],
      paths: {},
    },
  ],
};

export const PLAY_TEMPLATES = [pickRoll, horns, spainPnr, flex, princetonChin, zipper, stack];

export function getTemplate(id) {
  return PLAY_TEMPLATES.find(t => t.id === id) || null;
}
